/*
 * Cart gifts (snippets/cart-gifts.liquid, cart-gift-list.liquid, cart-gift-panel.liquid)
 *
 * - <cart-gift-panel>: "Choisir mon cadeau" drawer next to the cart drawer, with its
 *   own overlay. One gift per list; submitting replaces the gift of each list in the cart.
 * - <cart-gifts>: rendered inside the mini cart, so re-created on every cart update.
 *   It refreshes the panel lists and applies the gift line fixes computed in Liquid
 *   (gift removed when its condition is no longer met).
 *
 * <cart-gift-panel> is defined first so that it is upgraded before <cart-gifts>.
 */
const cartGiftsUtils = {
  bundledSections() {
    const sections = [];
    document.documentElement.dispatchEvent(
      new CustomEvent("cart:bundled-sections", {
        bubbles: true,
        detail: { sections },
      }),
    );
    return sections;
  },

  // /cart/add expects the "javascript" Accept header (as in theme.js), /cart/update "json"
  async post(url, body, type = "json") {
    const response = await fetch(url, {
      ...theme.utils.fetchConfig(type),
      body: JSON.stringify({
        ...body,
        sections_url: window.location.pathname,
      }),
    });
    const state = await response.json().catch(() => ({ status: response.status }));
    if (!response.ok || state.status) throw state;
    return state;
  },

  publish(cart) {
    theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
      source: "cart-gifts",
      cart,
    });
  },
};

if (!customElements.get("cart-gift-panel")) {
  customElements.define(
    "cart-gift-panel",
    class CartGiftPanel extends HTMLElement {
      connectedCallback() {
        this.abortController = new AbortController();
        const { signal } = this.abortController;

        this.form = this.querySelector("form");
        this.content = this.querySelector("[data-gift-panel-content]");
        this.error = this.querySelector("[data-gift-panel-error]");
        this.submitButton = this.form.querySelector('[type="submit"]');

        this.form.addEventListener("submit", this.onSubmit.bind(this), { signal });
        // Close button and overlay (which also covers the cart drawer)
        this.querySelectorAll("[data-gift-panel-close]").forEach((element) =>
          element.addEventListener("click", () => this.close(), { signal }),
        );

        // Escape closes the gifts drawer only: stopped before the cart drawer's
        // own Escape handler (keyup on document)
        window.addEventListener(
          "keyup",
          (event) => {
            if (event.code !== "Escape" || !this.isOpen) return;
            event.stopPropagation();
            this.close();
          },
          { capture: true, signal },
        );
        this.content.addEventListener("click", this.onRadioClick.bind(this), { signal });
        this.content.addEventListener("change", this.onVariantChange.bind(this), { signal });

        // The panel closes with the cart drawer
        this.closest("cart-drawer")?.addEventListener(
          "drawer:afterHide",
          () => this.close(false),
          { signal },
        );
      }

      disconnectedCallback() {
        this.abortController?.abort();
      }

      get isOpen() {
        return this.hasAttribute("open");
      }

      // Called by <cart-gifts> on every mini cart render
      update(listsHtml) {
        if (!listsHtml) {
          this.content.innerHTML = "";
          this.close(false);
          return;
        }

        this.content.innerHTML = listsHtml;
        this.content
          .querySelectorAll('input[type="radio"]')
          .forEach((radio) => (radio.dataset.checked = String(radio.checked)));
        this.hideError();
      }

      open(trigger = null) {
        if (!this.content.querySelector("[data-gift-list]")) return;

        // Same states as the theme drawers (ModalElement.showTransition):
        // [open] enables pointer events, [active] runs the transitions
        this.trigger = trigger;
        this.hidden = false;
        this.setAttribute("open", "");
        void this.offsetWidth; // reflow so the transitions start from the closed state
        clearTimeout(this.activeTimeout);
        this.activeTimeout = setTimeout(() => {
          if (this.isOpen) this.setAttribute("active", "");
        }, 75);
        this.setExpanded(true);
        this.querySelector("button[data-gift-panel-close]").focus({ preventScroll: true });
      }

      close(restoreFocus = true) {
        if (!this.isOpen) return;

        clearTimeout(this.activeTimeout);
        this.removeAttribute("active");
        this.removeAttribute("open");
        this.setExpanded(false);
        setTimeout(() => {
          if (!this.isOpen) this.hidden = true;
        }, 600);

        if (restoreFocus && this.trigger?.isConnected) {
          this.trigger.focus({ preventScroll: true });
        }
      }

      setExpanded(expanded) {
        document
          .querySelectorAll(`[aria-controls="${this.id}"]`)
          .forEach((button) => button.setAttribute("aria-expanded", String(expanded)));
      }

      // A selected gift can be unselected by clicking it again
      onRadioClick(event) {
        const radio = event.target.closest('input[type="radio"]');
        if (!radio) return;

        if (radio.dataset.checked === "true") {
          radio.checked = false;
          radio.dataset.checked = "false";
          return;
        }

        this.content
          .querySelectorAll(`input[type="radio"][name="${CSS.escape(radio.name)}"]`)
          .forEach((input) => (input.dataset.checked = "false"));
        radio.dataset.checked = "true";
      }

      // The variant picker sets the variant added for its product
      onVariantChange(event) {
        const select = event.target.closest("[data-gift-variant]");
        if (!select) return;

        const radio = select
          .closest("[data-gift-item]")
          .querySelector('input[type="radio"]');
        radio.value = select.value;
        radio.dataset.variantId = select.value;
      }

      async onSubmit(event) {
        event.preventDefault();
        // Own flag: the theme's hover-button sets aria-busy on submit before this handler runs
        if (this.submitting) return;

        const updates = {};
        const items = [];
        this.content.querySelectorAll("[data-gift-list]").forEach((list) => {
          const currentLine = list.dataset.currentLine;
          const currentVariant = list.dataset.currentVariant;
          const selected = list.querySelector('input[type="radio"]:checked')?.value || null;

          if (selected === currentVariant) return;
          if (currentLine) updates[currentLine] = 0;
          if (selected) {
            items.push({
              id: Number(selected),
              quantity: 1,
              properties: { _gift_offer: list.dataset.giftList },
            });
          }
        });

        console.info("[cart-gifts] submit", { updates, items });
        if (!Object.keys(updates).length && !items.length) {
          this.submitButton.removeAttribute("aria-busy");
          this.close();
          return;
        }

        this.hideError();
        this.submitting = true;
        this.submitButton.setAttribute("aria-busy", "true");

        try {
          const sections = cartGiftsUtils.bundledSections();
          let cart;

          if (Object.keys(updates).length) {
            cart = await cartGiftsUtils.post(theme.routes.cart_update_url, {
              updates,
              ...(items.length ? {} : { sections }),
            });
          }

          if (items.length) {
            const added = await cartGiftsUtils.post(
              theme.routes.cart_add_url,
              { items, sections },
              "javascript",
            );
            cart = await (
              await fetch(theme.routes.cart_url, {
                ...theme.utils.fetchConfig("json", "GET"),
              })
            ).json();
            cart.sections = added.sections;
            console.info("[cart-gifts] added", added);
          }

          this.close(false);
          cartGiftsUtils.publish(cart);
        } catch (error) {
          console.error("[cart-gifts]", error);
          this.showError(error?.description || error?.message);
        } finally {
          this.submitting = false;
          this.submitButton.removeAttribute("aria-busy");
        }
      }

      showError(message) {
        this.error.textContent = message || this.dataset.errorMessage || "";
        this.error.hidden = false;
      }

      hideError() {
        this.error.textContent = "";
        this.error.hidden = true;
      }
    },
  );
}

if (!customElements.get("cart-gifts")) {
  customElements.define(
    "cart-gifts",
    class CartGifts extends HTMLElement {
      static syncing = false;
      static failedSync = null;

      connectedCallback() {
        this.abortController = new AbortController();
        const panel = document.getElementById(
          `CartGiftPanel-${this.dataset.sectionId}`,
        );
        const template = this.querySelector("template[data-gift-lists]");

        if (typeof panel?.update === "function") {
          panel.update(template ? template.innerHTML : null);
        }

        this.querySelector("[data-gift-panel-open]")?.addEventListener(
          "click",
          (event) => {
            event.preventDefault();
            panel?.open(event.currentTarget);
          },
          { signal: this.abortController.signal },
        );

        this.syncGiftLines();
      }

      disconnectedCallback() {
        this.abortController?.abort();
      }

      // Removes gifts whose condition is no longer met (computed in Liquid)
      async syncGiftLines() {
        let updates;
        try {
          updates = JSON.parse(this.dataset.updates || "{}");
        } catch (error) {
          return;
        }

        const signature = JSON.stringify(updates);
        if (
          !Object.keys(updates).length ||
          CartGifts.syncing ||
          CartGifts.failedSync === signature
        ) {
          return;
        }

        console.info("[cart-gifts] sync gift lines", updates);
        CartGifts.syncing = true;
        try {
          const cart = await cartGiftsUtils.post(theme.routes.cart_update_url, {
            updates,
            sections: cartGiftsUtils.bundledSections(),
          });
          CartGifts.failedSync = null;
          CartGifts.syncing = false;
          cartGiftsUtils.publish(cart);
        } catch (error) {
          // Avoid retrying the same update on every render
          CartGifts.failedSync = signature;
          CartGifts.syncing = false;
          console.error(error);
        }
      }
    },
  );
}
